import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Trophy, Loader2, Hash, CheckCircle, PartyPopper,
  Volume2, VolumeX, User, Upload, Camera
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import { API_URL } from '../utils/api';

// UK Bingo number calls (traditional calls)
const UK_BINGO_CALLS = {
  1: "Kelly's Eye", 2: "One Little Duck", 3: "Cup of Tea", 4: "Knock at the Door",
  5: "Man Alive", 6: "Tom Mix", 7: "Lucky Seven", 8: "Garden Gate",
  9: "Doctor's Orders", 10: "Boris's Den", 11: "Legs Eleven", 12: "One Dozen",
  13: "Unlucky for Some", 14: "Valentine's Day", 15: "Young and Keen", 16: "Sweet Sixteen",
  17: "Dancing Queen", 18: "Coming of Age", 19: "Goodbye Teens", 20: "One Score",
  21: "Royal Salute", 22: "Two Little Ducks", 23: "Thee and Me", 24: "Two Dozen",
  25: "Duck and Dive", 26: "Pick and Mix", 27: "Gateway to Heaven", 28: "Over Weight",
  29: "Rise and Shine", 30: "Dirty Gertie", 31: "Get Up and Run", 32: "Buckle My Shoe",
  33: "All the Threes", 34: "Ask for More", 35: "Jump and Jive", 36: "Three Dozen",
  37: "More than Eleven", 38: "Christmas Cake", 39: "Steps", 40: "Naughty Forty",
  41: "Time for Fun", 42: "Winnie the Pooh", 43: "Down on Your Knees", 44: "Droopy Drawers",
  45: "Halfway There", 46: "Up to Tricks", 47: "Four and Seven", 48: "Four Dozen",
  49: "PC", 50: "Half a Century", 51: "Tweak of the Thumb", 52: "Danny La Rue",
  53: "Here Comes Herbie", 54: "Clean the Floor", 55: "All the Fives", 56: "Was She Worth It?",
  57: "Heinz Varieties", 58: "Make Them Wait", 59: "Brighton Line", 60: "Five Dozen",
  61: "Baker's Bun", 62: "Turn the Screw", 63: "Tickle Me", 64: "Red Raw",
  65: "Old Age Pension", 66: "Clickety Click", 67: "Made in Heaven", 68: "Saving Grace",
  69: "Either Way Up", 70: "Three Score and Ten", 71: "Bang on the Drum", 72: "Six Dozen",
  73: "Queen Bee", 74: "Candy Store", 75: "Strive and Strive", 76: "Trombones",
  77: "Sunset Strip", 78: "Heaven's Gate", 79: "One More Time", 80: "Eight and Blank",
  81: "Stop and Run", 82: "Straight On Through", 83: "Time for Tea", 84: "Seven Dozen",
  85: "Staying Alive", 86: "Between the Sticks", 87: "Torquay in Devon", 88: "Two Fat Ladies",
  89: "Nearly There", 90: "Top of the Shop"
};

// Preset avatar options
const PRESET_AVATARS = [
  { id: 'cat', emoji: '🐱', label: 'Cat' },
  { id: 'dog', emoji: '🐕', label: 'Dog' },
  { id: 'star', emoji: '⭐', label: 'Star' },
  { id: 'heart', emoji: '❤️', label: 'Heart' },
  { id: 'crown', emoji: '👑', label: 'Crown' },
  { id: 'fire', emoji: '🔥', label: 'Fire' },
  { id: 'rainbow', emoji: '🌈', label: 'Rainbow' },
  { id: 'music', emoji: '🎵', label: 'Music' },
  { id: 'dancer', emoji: '💃', label: 'Dancer' },
  { id: 'clover', emoji: '🍀', label: 'Lucky' },
  { id: 'dice', emoji: '🎲', label: 'Dice' },
  { id: 'trophy', emoji: '🏆', label: 'Trophy' },
];

export default function BingoUK90PlayPage() {
  const { gameCode } = useParams();
  
  const [nickname, setNickname] = useState('');
  const [avatarType, setAvatarType] = useState('preset'); // 'preset' or 'custom'
  const [avatarValue, setAvatarValue] = useState('star'); // preset id or custom URL
  const [playerId, setPlayerId] = useState(null);
  const [card, setCard] = useState(null);
  const [cards, setCards] = useState(null); // Multiple cards for multi-ticket
  const [ticketCount, setTicketCount] = useState(1);
  const [markedNumbers, setMarkedNumbers] = useState([]);
  const [calledNumbers, setCalledNumbers] = useState([]);
  const [gameStatus, setGameStatus] = useState('waiting');
  const [winPattern, setWinPattern] = useState('one_line');
  const [winPatterns, setWinPatterns] = useState(null);
  const [currentPatternIndex, setCurrentPatternIndex] = useState(0);
  const [patternWinners, setPatternWinners] = useState({});
  const [winners, setWinners] = useState([]);
  const [joining, setJoining] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [lastCalledNumber, setLastCalledNumber] = useState(null);
  const [wonPatterns, setWonPatterns] = useState([]); // Track which patterns this player has won

  // Poll for game updates
  const fetchPlayerState = useCallback(async () => {
    if (!playerId || !gameCode) return;
    
    try {
      const res = await fetch(`${API_URL}/api/bingo/games/${gameCode}/card/${playerId}`);
      if (res.ok) {
        const data = await res.json();
        
        // Check for new number called
        if (data.called_numbers?.length > calledNumbers.length) {
          const newNumber = data.called_numbers[data.called_numbers.length - 1];
          setLastCalledNumber(newNumber);
          
          // Speak the number
          if (soundEnabled && 'speechSynthesis' in window) {
            const call = UK_BINGO_CALLS[newNumber] || '';
            const text = `${newNumber}. ${call}`;
            const utterance = new SpeechSynthesisUtterance(text);
            utterance.rate = 0.8;
            speechSynthesis.speak(utterance);
          }
        }
        
        setCard(data.card);
        setCards(data.cards); // Multiple cards
        setTicketCount(data.ticket_count || 1);
        setMarkedNumbers(data.marked);
        setCalledNumbers(data.called_numbers || []);
        setGameStatus(data.status);
        setWinPattern(data.win_pattern);
        setWinPatterns(data.win_patterns);
        setCurrentPatternIndex(data.current_pattern_index || 0);
        setPatternWinners(data.pattern_winners || {});
        setWinners(data.winners || []);
        
        // Check if we won the current pattern (for confetti celebration)
        const currentPattern = data.win_pattern || winPattern;
        const weWonCurrentPattern = data.pattern_winners?.[currentPattern]?.player_id === playerId;
        
        if (weWonCurrentPattern && !wonPatterns.includes(currentPattern)) {
          setWonPatterns(prev => [...prev, currentPattern]);
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
          });
        }
      }
    } catch (error) {
      console.error('Error fetching state:', error);
    }
  }, [playerId, gameCode, calledNumbers.length, soundEnabled, wonPatterns, winPattern]);

  useEffect(() => {
    if (!playerId) return;
    
    fetchPlayerState();
    const interval = setInterval(fetchPlayerState, 1500);
    return () => clearInterval(interval);
  }, [playerId, fetchPlayerState]);

  // Load saved player from localStorage
  useEffect(() => {
    const savedPlayer = localStorage.getItem(`bingo_uk90_player_${gameCode}`);
    if (savedPlayer) {
      const { playerId: savedId, nickname: savedNick } = JSON.parse(savedPlayer);
      setPlayerId(savedId);
      setNickname(savedNick);
    }
  }, [gameCode]);

  const joinGame = async () => {
    if (!nickname.trim()) {
      toast.error('Please enter a nickname');
      return;
    }
    
    setJoining(true);
    try {
      const res = await fetch(`${API_URL}/api/bingo/games/${gameCode}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          nickname: nickname.trim(),
          avatar_type: avatarType,
          avatar_value: avatarValue
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setPlayerId(data.player_id);
        setCard(data.card);
        setCards(data.cards); // Multiple cards
        setTicketCount(data.ticket_count || 1);
        setMarkedNumbers([]);
        
        // Save to localStorage
        localStorage.setItem(`bingo_uk90_player_${gameCode}`, JSON.stringify({
          playerId: data.player_id,
          nickname: nickname.trim()
        }));
        
        toast.success(`Joined with ${data.ticket_count || 1} ticket(s)!`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to join');
      }
    } catch (error) {
      toast.error('Failed to join game');
    } finally {
      setJoining(false);
    }
  };

  // Handle custom avatar upload
  const handleAvatarUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 500000) { // 500KB limit
      toast.error('Image too large (max 500KB)');
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarType('custom');
      setAvatarValue(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const markNumber = async (number) => {
    if (!calledNumbers.includes(number)) {
      toast.error("This number hasn't been called yet!");
      return;
    }
    if (markedNumbers.includes(number)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/bingo/games/${gameCode}/mark`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ player_id: playerId, number: number })
      });
      
      if (res.ok) {
        setMarkedNumbers(prev => [...prev, number]);
      }
    } catch (error) {
      console.error('Error marking number:', error);
    }
  };

  const claimWin = async () => {
    setClaiming(true);
    try {
      const res = await fetch(`${API_URL}/api/bingo/games/${gameCode}/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ player_id: playerId })
      });
      
      if (res.ok) {
        // Win is tracked via wonPatterns state which triggers confetti in fetchPlayerState
        confetti({
          particleCount: 150,
          spread: 100,
          origin: { y: 0.6 }
        });
        toast.success('BINGO! You win!');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Invalid bingo claim');
      }
    } catch (error) {
      toast.error('Failed to claim win');
    } finally {
      setClaiming(false);
    }
  };

  const checkPotentialWin = () => {
    if (!card || !markedNumbers) return false;
    
    const marked = new Set(markedNumbers);
    
    // Check each row
    let completeRows = 0;
    for (const row of card) {
      const rowNumbers = row.filter(cell => cell !== null);
      if (rowNumbers.every(num => marked.has(num))) {
        completeRows++;
      }
    }
    
    if (winPattern === 'one_line') return completeRows >= 1;
    if (winPattern === 'two_lines') return completeRows >= 2;
    if (winPattern === 'full_house') return completeRows >= 3;
    
    return false;
  };

  // Can only claim if: we have the pattern, haven't won THIS pattern yet, and no one else has won this pattern
  const hasWonCurrentPattern = wonPatterns.includes(winPattern) || patternWinners[winPattern]?.player_id === playerId;
  const someoneElseWonCurrentPattern = patternWinners[winPattern] && patternWinners[winPattern].player_id !== playerId;
  const canClaimWin = checkPotentialWin() && !hasWonCurrentPattern && !someoneElseWonCurrentPattern;

  // Join form with avatar selection
  if (!playerId) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-900/30 to-slate-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-900 border-slate-800">
          <CardContent className="p-6 space-y-4">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Hash className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white">Join UK Bingo</h1>
              <p className="text-slate-400 mt-1">Game Code: <span className="font-mono text-amber-400">{gameCode}</span></p>
            </div>
            
            <Input
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="Enter your nickname"
              className="bg-slate-800 border-slate-700 text-white text-center text-lg h-12"
              maxLength={20}
              onKeyPress={(e) => e.key === 'Enter' && joinGame()}
            />
            
            {/* Avatar Selection */}
            <div>
              <label className="text-sm text-slate-400 block mb-2">Choose your avatar</label>
              <div className="grid grid-cols-6 gap-2 mb-3">
                {PRESET_AVATARS.map(avatar => (
                  <button
                    key={avatar.id}
                    onClick={() => {
                      setAvatarType('preset');
                      setAvatarValue(avatar.id);
                    }}
                    className={`p-2 text-2xl rounded-lg border-2 transition-all ${
                      avatarType === 'preset' && avatarValue === avatar.id
                        ? 'border-amber-500 bg-amber-900/30 scale-110'
                        : 'border-slate-700 hover:border-slate-500'
                    }`}
                    title={avatar.label}
                  >
                    {avatar.emoji}
                  </button>
                ))}
              </div>
              
              {/* Custom upload option */}
              <div className="flex items-center gap-2">
                <label className="flex-1 cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    className="hidden"
                  />
                  <div className={`flex items-center justify-center gap-2 p-2 rounded-lg border-2 transition-colors ${
                    avatarType === 'custom' 
                      ? 'border-amber-500 bg-amber-900/30' 
                      : 'border-slate-700 hover:border-slate-500'
                  }`}>
                    {avatarType === 'custom' && avatarValue ? (
                      <img src={avatarValue} alt="Avatar" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <Camera className="w-5 h-5 text-slate-400" />
                    )}
                    <span className="text-sm text-slate-400">
                      {avatarType === 'custom' ? 'Change photo' : 'Upload photo'}
                    </span>
                  </div>
                </label>
              </div>
            </div>
            
            <Button
              onClick={joinGame}
              disabled={joining}
              className="w-full bg-amber-600 hover:bg-amber-500 h-12 text-lg"
            >
              {joining ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Join Game'
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Winner screen - only show after winning full_house (the final pattern)
  const wonFullHouse = wonPatterns.includes('full_house');
  if (wonFullHouse) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-900/30 to-slate-950 flex items-center justify-center p-4">
        <div className="text-center">
          <PartyPopper className="w-24 h-24 mx-auto text-amber-400 mb-4 animate-bounce" />
          <h1 className="text-5xl font-bold text-white mb-2">FULL HOUSE!</h1>
          <p className="text-2xl text-amber-400">Congratulations, {nickname}!</p>
          <p className="text-slate-400 mt-4">You won the Full House!</p>
          {wonPatterns.length > 1 && (
            <p className="text-green-400 mt-2">
              Also won: {wonPatterns.filter(p => p !== 'full_house').map(p => 
                p === 'one_line' ? '1 Line' : '2 Lines'
              ).join(', ')}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Someone else won full house - game ends for everyone
  const someoneElseWonFullHouse = patternWinners['full_house'] && patternWinners['full_house'].player_id !== playerId;
  if (someoneElseWonFullHouse) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-900/30 to-slate-950 flex items-center justify-center p-4">
        <div className="text-center">
          <Trophy className="w-20 h-20 mx-auto text-amber-400 mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">Game Over!</h1>
          <p className="text-xl text-amber-400">{patternWinners['full_house'].nickname} wins Full House!</p>
          {wonPatterns.length > 0 && (
            <p className="text-green-400 mt-4">
              You won: {wonPatterns.map(p => 
                p === 'one_line' ? '1 Line' : p === 'two_lines' ? '2 Lines' : 'Full House'
              ).join(', ')}
            </p>
          )}
          {wonPatterns.length === 0 && (
            <p className="text-slate-400 mt-4">Better luck next round!</p>
          )}
        </div>
      </div>
    );
  }

  // Game card - Traditional UK Layout (9 columns x 3 rows)
  return (
    <div className="min-h-screen bg-slate-950 p-2 sm:p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            {/* Avatar display */}
            <div className="w-10 h-10 rounded-full bg-amber-600 flex items-center justify-center text-xl">
              {avatarType === 'custom' && avatarValue ? (
                <img src={avatarValue} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                PRESET_AVATARS.find(a => a.id === avatarValue)?.emoji || '⭐'
              )}
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">{nickname}</h1>
              <p className="text-xs text-slate-400">
                {winPatterns && winPatterns.length > 1 ? (
                  <>Playing for: {(winPatterns[currentPatternIndex] || 'one_line').replace('_', ' ')}</>
                ) : (
                  winPattern === 'one_line' ? 'Complete 1 row!' : 
                  winPattern === 'two_lines' ? 'Complete 2 rows!' : 'Complete all rows!'
                )}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="p-2 text-slate-400 hover:text-white"
            >
              {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
            <Badge className={gameStatus === 'active' ? 'bg-green-600' : 'bg-yellow-600'}>
              {gameStatus === 'active' ? 'Playing' : 'Waiting'}
            </Badge>
          </div>
        </div>

        {/* Pattern Progress for Progressive Play */}
        {winPatterns && winPatterns.length > 1 && (
          <div className="flex gap-1 mb-3 overflow-x-auto pb-1">
            {winPatterns.map((pattern, idx) => {
              const winner = patternWinners?.[pattern];
              const isCurrent = idx === currentPatternIndex;
              return (
                <div
                  key={pattern}
                  className={`flex-shrink-0 px-2 py-1 rounded text-xs ${
                    winner ? 'bg-green-900/50 text-green-400' :
                    isCurrent ? 'bg-amber-900/50 text-amber-400 border border-amber-500' :
                    'bg-slate-800 text-slate-400'
                  }`}
                >
                  {pattern.replace('_', ' ')}
                  {winner && ` - ${winner.nickname}`}
                </div>
              );
            })}
          </div>
        )}

        {/* Current Number Banner */}
        {lastCalledNumber && gameStatus === 'active' && (
          <div className="bg-amber-600 rounded-lg p-3 mb-3 text-center animate-pulse">
            <div className="text-xs text-amber-200">JUST CALLED</div>
            <div className="text-3xl font-bold text-white">{lastCalledNumber}</div>
            <div className="text-sm text-amber-200">{UK_BINGO_CALLS[lastCalledNumber] || ''}</div>
          </div>
        )}

        {/* Claim Win Button */}
        {canClaimWin && (
          <Button
            onClick={claimWin}
            disabled={claiming}
            className="w-full mb-3 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-bold h-14 text-xl animate-pulse"
          >
            {claiming ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              <>
                <Trophy className="w-6 h-6 mr-2" />
                CLAIM BINGO!
              </>
            )}
          </Button>
        )}

        {/* UK Bingo Card(s) - 9 columns x 3 rows each */}
        {(cards || [card]).filter(Boolean).map((singleCard, cardIdx) => (
          <div key={cardIdx} className="mb-4">
            {ticketCount > 1 && (
              <div className="text-center text-sm text-amber-400 font-semibold mb-1">
                Ticket {cardIdx + 1} of {ticketCount}
              </div>
            )}
            <div className="bg-amber-50 rounded-xl p-2 border-4 border-amber-500">
              <div className="space-y-1">
                {singleCard.map((row, rowIdx) => (
                  <div key={rowIdx} className="grid grid-cols-9 gap-1">
                    {row.map((cell, colIdx) => {
                      const isNumber = cell !== null;
                      const isMarked = isNumber && markedNumbers.includes(cell);
                      const isCalled = isNumber && calledNumbers.includes(cell);
                      
                      return (
                        <button
                          key={`${cardIdx}-${rowIdx}-${colIdx}`}
                          onClick={() => isNumber && markNumber(cell)}
                          disabled={!isNumber || isMarked || !isCalled}
                          className={`
                            h-12 sm:h-14 rounded flex items-center justify-center
                            text-lg sm:text-xl font-bold
                            transition-all duration-200
                            ${!isNumber ? 'bg-amber-100' : ''}
                            ${isNumber && isMarked ? 'bg-red-600 text-white scale-95' : ''}
                            ${isNumber && !isMarked && isCalled ? 'bg-amber-400 text-black border-2 border-amber-600 animate-pulse' : ''}
                            ${isNumber && !isMarked && !isCalled ? 'bg-white text-slate-900 border border-amber-300' : ''}
                          `}
                        >
                          {isNumber && (isMarked ? (
                            <CheckCircle className="w-6 h-6" />
                          ) : (
                            cell
                          ))}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}

        {/* Legend */}
        <div className="mt-3 flex justify-center gap-4 text-xs text-slate-400">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-amber-400 border border-amber-600"></div>
            <span>Called - tap!</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-red-600"></div>
            <span>Marked</span>
          </div>
        </div>

        {/* Called Count */}
        <div className="mt-3 text-center text-sm text-slate-400">
          {calledNumbers.length}/90 numbers called
        </div>

        {/* Your Win Celebration Banner - shown when you win a pattern but game continues */}
        {wonPatterns.length > 0 && !wonPatterns.includes('full_house') && (
          <div className="mt-4 bg-green-900/50 border border-green-600 rounded-lg p-3">
            <div className="text-green-400 text-center">
              <p className="text-lg font-bold flex items-center justify-center gap-2">
                <PartyPopper className="w-5 h-5" />
                You won {wonPatterns.map(p => p === 'one_line' ? '1 Line' : '2 Lines').join(' & ')}!
                <PartyPopper className="w-5 h-5" />
              </p>
              <p className="text-sm text-green-300 mt-1">Keep playing for {winPattern === 'two_lines' ? '2 Lines' : 'Full House'}!</p>
            </div>
          </div>
        )}

        {/* Winners Banner (other players' wins) */}
        {/* Other Winners Banner - show other players who won patterns */}
        {Object.keys(patternWinners).length > 0 && (
          <div className="mt-4 bg-amber-900/30 rounded-lg p-3">
            <div className="text-amber-400 text-sm font-medium text-center">
              {Object.entries(patternWinners)
                .filter(([pattern, winner]) => winner.player_id !== playerId)
                .map(([pattern, winner], i) => (
                <span key={pattern}>
                  {i > 0 && ' • '}
                  {winner.nickname} won {pattern === 'one_line' ? '1 Line' : pattern === 'two_lines' ? '2 Lines' : 'Full House'}!
                </span>
              ))}
              {Object.values(patternWinners).every(w => w.player_id === playerId) && wonPatterns.length > 0 && (
                <span className="text-green-400">You're in the lead! Keep going!</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
